import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import useApi from "../../hooks/useApi";
import Notification from "../../components/common/Notification"; // <-- Import Notification

const TABS = [
  { key: "general", label: "General Settings" },
  { key: "social", label: "Social Links" },
  { key: "apps", label: "App Downloads" },
];

const initialSettings = {
  siteName: "",
  supportEmail: "",
  supportPhone: "",
  termsOfServiceUrl: "",
  privacyPolicyUrl: "",
  contactAddress: "",
  contactPhone: "",
  contactEmail: "",
  contactWebsite: "",
  androidAppUrl: "",
  iosAppUrl: "",
  windowsAppUrl: "",
  defaultLanguage: "",
  defaultCurrency: "USD",
  defaultTimezone: "",
  defaultDateFormat: "",
  defaultTimeFormat: "",
  maintenanceMode: false,
  maintenanceMessage: "",
  announcement: "",
  // Social
  facebookUrl: "",
  twitterUrl: "",
  instagramUrl: "",
  linkedinUrl: "",
  youtubeUrl: "",
  redditUrl: "",
  telegramUrl: "",
  discordUrl: "",
  slackUrl: "",
  whatsappUrl: "",
};

const PlatformSettingsPage = ({ noLayout }) => {
  const api = useApi();
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // <-- Add messageType
  const [androidFile, setAndroidFile] = useState(null);
  const [windowsFile, setWindowsFile] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get("business-admin/platform-settings");
        setSettings({ ...initialSettings, ...res });
      } catch (err) {
        setMessage("Failed to load settings.");
        setMessageType("error");
      }
      setLoading(false);
    };
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e, type) => {
    if (type === "android") setAndroidFile(e.target.files[0]);
    if (type === "windows") setWindowsFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      // Handle file uploads if any
      let androidAppUrl = settings.androidAppUrl;
      let windowsAppUrl = settings.windowsAppUrl;
      if (androidFile) {
        const formData = new FormData();
        formData.append("file", androidFile);
        const res = await api.post("business-admin/platform-settings/upload/android", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        androidAppUrl = res.url || res.androidAppUrl;
      }
      if (windowsFile) {
        const formData = new FormData();
        formData.append("file", windowsFile);
        const res = await api.post("business-admin/platform-settings/upload/windows", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        windowsAppUrl = res.url || res.windowsAppUrl;
      }
      // Save settings
      await api.put("business-admin/platform-settings", {
        ...settings,
        androidAppUrl,
        windowsAppUrl,
      });
      setMessage("Settings updated successfully.");
      setMessageType("success");
      setAndroidFile(null);
      setWindowsFile(null);
    } catch (err) {
      setMessage("Failed to update settings.");
      setMessageType("error");
    }
    setSaving(false);
  };

  const renderGeneral = () => (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
      <Notification
        type={messageType}
        message={message}
        onClose={() => setMessage("")}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
        <input type="text" name="siteName" value={settings.siteName} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
        <input type="email" name="supportEmail" value={settings.supportEmail} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Support Phone</label>
        <input type="text" name="supportPhone" value={settings.supportPhone} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Terms of Service URL</label>
        <input type="url" name="termsOfServiceUrl" value={settings.termsOfServiceUrl} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Policy URL</label>
        <input type="url" name="privacyPolicyUrl" value={settings.privacyPolicyUrl} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Address</label>
        <input type="text" name="contactAddress" value={settings.contactAddress} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
        <input type="text" name="contactPhone" value={settings.contactPhone} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
        <input type="email" name="contactEmail" value={settings.contactEmail} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Website</label>
        <input type="url" name="contactWebsite" value={settings.contactWebsite} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
          <input type="text" name="defaultLanguage" value={settings.defaultLanguage} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="en" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
          <input type="text" name="defaultCurrency" value={settings.defaultCurrency} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="USD" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Timezone</label>
          <input type="text" name="defaultTimezone" value={settings.defaultTimezone} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="Asia/Kolkata" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
          <input type="text" name="defaultDateFormat" value={settings.defaultDateFormat} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="YYYY-MM-DD" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Time Format</label>
        <input type="text" name="defaultTimeFormat" value={settings.defaultTimeFormat} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="HH:mm:ss" />
      </div>
      <div className="flex items-center">
        <input type="checkbox" name="maintenanceMode" checked={settings.maintenanceMode} onChange={handleChange} className="mr-2" />
        <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Message</label>
        <input type="text" name="maintenanceMessage" value={settings.maintenanceMessage} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Announcement</label>
        <textarea name="announcement" value={settings.announcement} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" rows={3} />
      </div>
      <button type="submit" disabled={saving} className="w-full bg-indigo-600 text-white py-2 rounded-md font-semibold hover:bg-indigo-700 transition">
        {saving ? "Saving..." : "Save General Settings"}
      </button>
    </form>
  );

  const renderSocial = () => (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
      <Notification
        type={messageType}
        message={message}
        onClose={() => setMessage("")}
      />
      <h3 className="text-lg font-semibold mb-4">Social Links</h3>
      {{
        "facebookUrl": "Facebook",
        "twitterUrl": "Twitter",
        "instagramUrl": "Instagram",
        "linkedinUrl": "LinkedIn",
        "youtubeUrl": "YouTube",
        "redditUrl": "Reddit",
        "telegramUrl": "Telegram",
        "discordUrl": "Discord",
        "slackUrl": "Slack",
        "whatsappUrl": "WhatsApp",
      }[Object.keys(settings).find(key => settings[key] === "")] && (
        <div className="text-sm text-gray-500 mb-4">
          Please complete all fields before saving.
        </div>
      )}
      {Object.keys(settings).map((key) => {
        if (!key.endsWith("Url")) return null;
        return (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {key.replace("Url", "").replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
            </label>
            <input
              type="url"
              name={key}
              value={settings[key]}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder={`https://...`}
            />
          </div>
        );
      })}
      <button type="submit" disabled={saving} className="w-full bg-indigo-600 text-white py-2 rounded-md font-semibold hover:bg-indigo-700 transition">
        {saving ? "Saving..." : "Save Social Links"}
      </button>
    </form>
  );

  const renderApps = () => (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
      <Notification
        type={messageType}
        message={message}
        onClose={() => setMessage("")}
      />
      <h3 className="text-lg font-semibold mb-4">App Downloads</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Android App URL</label>
        <input
          type="url"
          name="androidAppUrl"
          value={settings.androidAppUrl}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-md p-2 mb-2"
          placeholder="https://play.google.com/..."
        />
        <input
          type="file"
          accept=".apk"
          onChange={e => handleFileChange(e, "android")}
          className="block"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">iOS App URL</label>
        <input
          type="url"
          name="iosAppUrl"
          value={settings.iosAppUrl}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-md p-2"
          placeholder="https://apps.apple.com/..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Windows App URL</label>
        <input
          type="url"
          name="windowsAppUrl"
          value={settings.windowsAppUrl}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-md p-2 mb-2"
          placeholder="https://..."
        />
        <input
          type="file"
          accept=".exe,.msi"
          onChange={e => handleFileChange(e, "windows")}
          className="block"
        />
      </div>
      <button type="submit" disabled={saving} className="w-full bg-indigo-600 text-white py-2 rounded-md font-semibold hover:bg-indigo-700 transition">
        {saving ? "Saving..." : "Save App Settings"}
      </button>
    </form>
  );

  const content = (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.key
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      {loading ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : (
        <>
          {activeTab === "general" && renderGeneral()}
          {activeTab === "social" && renderSocial()}
          {activeTab === "apps" && renderApps()}
        </>
      )}
    </div>
  );
  if (noLayout) return content;
  return <Layout>{content}</Layout>;
};

export default PlatformSettingsPage;